import { useState } from 'react';

import { Select, Tag, Tooltip } from 'antd';
import './author-select.less';
import { Link } from 'react-router-dom';
import { ExportOutlined } from '@ant-design/icons';
import { useGetAuthorsInfiniteInfiniteQuery, useGetAuthorQuery } from 'services';
import useDebounce from 'hooks/useDebounce';
import ForeignAuthorTag from 'components/ForeignAuthorTag';

const { Option } = Select;

const AuthorCommiteeName = ({ committeeShortName, commiteeName }) => {
  return (
    <Tooltip
      trigger={['hover', 'focus']}
      title={commiteeName}
    >
      <Tag color="blue">{committeeShortName}</Tag>
    </Tooltip>
  );
};

/**
 * Bir yazarı `<Option>` olarak render eder. Hem infinite scroll ile yüklenen
 * sayfalar hem de seçili değer için iğnelenen tek yazar bu fonksiyonu kullanır.
 *
 * @param {Object} author - API'den dönen yazar kaydı (`@id`, `id`, `user` alanları).
 * @returns {JSX.Element}
 */
const renderAuthorOption = (author) => (
  <Option
    key={author['@id']}
    value={author['@id']}
  >
    <div className="gx-d-flex gx-align-items-center gx-justify-content-between">
      <div className="gx-d-flex gx-align-items-center  gap-2">
        <div>
          {author?.user?.firstName} {author?.user?.lastName} ({author?.user?.email})
        </div>
        {author?.user?.memberships.map(({ committee }) => (
          <AuthorCommiteeName
            key={committee['@id']}
            committeeShortName={committee.shortName}
            commiteeName={committee.name}
          />
        ))}
        <ForeignAuthorTag isForeign={author?.isForeign} />
      </div>
      <div>
        <Link
          className="gx-d-flex gx-justify-content-center gx-align-items-center"
          to={`/yazar-islemleri/yazar-duzenle/${author.id}`}
          target="_blank"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <ExportOutlined />
        </Link>
      </div>
    </div>
  </Option>
);

/**
 * Yazar/Güncelleyen seçimi için arama yapılabilir dropdown.
 *
 * Yazarları infinite scroll ile sayfa sayfa çeker (`itemsPerPage: 20`); böylece
 * büyük listelerde tek seferde tüm kayıtları çekmekten doğan timeout'u önler.
 * Arama server-side yapılır (`searchKey` parametresi, debounce'lu).
 *
 * Düzenleme akışında `value` önceden dolu (IRI) gelebilir; bu durumda seçili yazar
 * yüklenen sayfalarda olmayabileceğinden, tek kayıtlık istekle çekilip listeye
 * iğnelenir — aksi halde antd Select etiket yerine ham IRI gösterirdi.
 *
 * @param {Object} props
 * @param {boolean} [props.editable=true] - false ise alan salt-okunur (disabled) olur.
 * @param {string} [props.value] - Seçili yazarın IRI'si (`@id`). Form.Item enjekte eder.
 * @param {boolean} [props.disabled=false] - Alanı dışarıdan devre dışı bırakır.
 * @param {(value: string) => void} [props.onChange] - Seçim değişiminde çağrılır. Form.Item enjekte eder.
 */
const AuthorSelect = ({ editable = true, value, disabled = false, onChange }) => {
  const [searchText, setSearchText] = useState('');
  const { handleDebounce } = useDebounce();

  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } = useGetAuthorsInfiniteInfiniteQuery({
    search: searchText,
  });

  // Seçili değer (IRI) için tek yazarı çekip listeye iğneliyoruz; yüklenen
  // sayfalarda bulunmasa bile etiketin doğru görünmesini garanti eder.
  const selectedId = value ? value.split('/').pop() : undefined;
  const { data: selectedAuthor } = useGetAuthorQuery(selectedId, { skip: !selectedId });

  const rawAuthors = (data?.pages ?? []).flatMap((page) => page['hydra:member']);
  if (selectedAuthor) {
    rawAuthors.unshift(selectedAuthor);
  }

  // Sayfalar arası tekrar eden kayıtları ve iğnelenen seçili yazarı `@id` bazında tekilleştiriyoruz.
  const authors = Array.from(new Map(rawAuthors.map((author) => [author['@id'], author])).values());

  const handleSearch = (input) => {
    handleDebounce({
      callback: () => {
        setSearchText(input);
      },
    });
  };

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20 && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Select
      value={value}
      onChange={onChange}
      showSearch
      loading={isFetching}
      popupClassName="author-select-popup"
      dropdownMatchSelectWidth={false}
      filterOption={false}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      listHeight={500}
      disabled={!editable || disabled}
    >
      {authors.map((author) => renderAuthorOption(author))}
    </Select>
  );
};

export default AuthorSelect;
